import {
  api_portalOfficeHospitalListing_responseFilter,
  ibow2_proto_RequestUserFavorite,
} from '@/init/swagger';
import {
  SearchHospitalsResolver,
  SearchHospitalsType,
} from '@/schemas/SearchParamsSchemas/app/stations/search/hospitals/Schema';
import { useForm } from 'react-hook-form';
import { HOSPITAL_SEARCH_FILTER } from '@/types/types';
import { buildSearchParamsString } from '@/helpers/searchHospitalListingsHelper';
import { useState, useRef, useCallback } from 'react';
import { useNextRouter } from '@/hooks/useNextRouter';
import { pathInfo } from '@/types/pathInfo';
import { ParsedAppStationsSearchPageSearchParams } from '@/schemas/SearchParamsSchemas/app/stations/search/Schema';
import { ParsedPublicStationsSearchPageSearchParams } from '@/schemas/SearchParamsSchemas/public/stations/search/Schema';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import { HospitalListingListRef } from '../HospitalSearchResults';

type Props = {
  formValues: SearchHospitalsType;
  hospitalSearchFilters: api_portalOfficeHospitalListing_responseFilter[];
  isPublicSearch?: boolean;
  favorites: Array<ibow2_proto_RequestUserFavorite>;
  parsedSearchParams:
    | ParsedAppStationsSearchPageSearchParams
    | ParsedPublicStationsSearchPageSearchParams;
  hospitalListings: HospitalListingOutput[];
};

export const HOSPITAL_SEARCH_FORM_MODE = ['search', 'multiRequest'] as const;
export type HOSPITAL_SEARCH_FORM_MODE =
  (typeof HOSPITAL_SEARCH_FORM_MODE)[number];

export const useLogic = ({
  formValues,
  hospitalSearchFilters,
  isPublicSearch,
  favorites,
  parsedSearchParams,
  hospitalListings,
}: Props) => {
  const changeMode = (mode: HOSPITAL_SEARCH_FORM_MODE) => {
    const newQuery = new URLSearchParams(window.location.search);

    if (mode === 'search') {
      newQuery.delete(pathInfo['/app/stations/search'].searchParams.mode);
    } else {
      newQuery.set(pathInfo['/app/stations/search'].searchParams.mode, mode);
    }

    router.push(`/app/stations/search?${newQuery.toString()}`);
  };

  const [localFavorites, setLocalFavorites] = useState(favorites);

  const [isShowMap, setIsShowMap] = useState(
    parsedSearchParams.isShowMap || false,
  );

  // マップとリストの同期のための状態とref
  // Map表示時は最初のステーションが選択される
  const [mapMarkerPortalOfficeId, setMapMarkerPortalOfficeId] = useState<
    number | null
  >(hospitalListings[0]?.portal_office_id || null);
  const hospitalListingListRef = useRef<HospitalListingListRef>(null);

  // スクロール機能を実装
  const scrollToListing = useCallback(
    (id: number, source: 'hover' | 'click') => {
      if (hospitalListingListRef.current) {
        hospitalListingListRef.current.scrollToListing(id, source);
      } else {
        // フォールバック：ドキュメント全体から検索
        const targetElement = document.querySelector(`#hospital-${id}`);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        } else {
          console.warn(`Element with id "hospital-${id}" not found`);
        }
      }
    },
    [],
  );

  const router = useNextRouter();
  const method = useForm<SearchHospitalsType>({
    resolver: SearchHospitalsResolver,
    defaultValues: formValues,
  });

  const hospitalMedicalCares = hospitalSearchFilters.filter(
    (filter) =>
      filter.category_id === HOSPITAL_SEARCH_FILTER.MEDICAL_CARE_CATEGORY_ID,
  );

  const hospitalAppealPoints = hospitalSearchFilters.filter(
    (filter) =>
      filter.category_id === HOSPITAL_SEARCH_FILTER.APPEAL_POINT_CATEGORY_ID,
  );

  const search = ({
    event,
    keepPage = false,
  }: {
    event?: React.FormEvent<HTMLFormElement>;
    keepPage: boolean;
  }) => {
    event?.preventDefault();
    const formValues = method.getValues();
    if (!keepPage) {
      method.setValue('page', 1);
    }
    const searchParams = buildSearchParamsString({ formValues });

    router.push(
      `/${isPublicSearch ? 'public' : 'app'}/stations/search?${searchParams}`,
    );
  };

  const toggleIsShowMap = () => {
    const newQuery = new URLSearchParams(window.location.search);
    const newIsShowMap = !isShowMap;

    if (newIsShowMap) {
      newQuery.set('isShowMap', 'true');
      // Map表示時は最初のステーションを選択
      if (hospitalListings.length > 0) {
        setMapMarkerPortalOfficeId(hospitalListings[0].portal_office_id);
      }
    } else {
      newQuery.delete('isShowMap');
      setMapMarkerPortalOfficeId(null);
    }

    const baseUrl = isPublicSearch
      ? '/public/stations/search'
      : '/app/stations/search';
    router.push(`${baseUrl}?${newQuery.toString()}`);

    setIsShowMap(newIsShowMap);
  };

  const gotoPageForPagination = async (page: number) => {
    method.setValue('page', page);

    // ページング時にListingListのスクロールを最上部に移動
    if (hospitalListingListRef.current) {
      // 最初の要素がない場合もあるので、scrollToTopを実装している場合はそれを使用
      // そうでなければ0番目にスクロール
      try {
        if ('scrollToTop' in hospitalListingListRef.current) {
          (hospitalListingListRef.current as any).scrollToTop();
        } else {
          hospitalListingListRef.current.scrollToListing(
            hospitalListings[0]?.portal_office_id || 0,
            'click',
          );
        }
      } catch (_error) {
        // フォールバック
        window.scrollTo(0, 0);
      }
    }

    // 病院の選択は新しいデータ読み込み後に自動的に行われるため、ここでは実行しない

    search({ keepPage: true });
  };

  // スクロールなしのホバー処理（HospitalListingItem用）
  const setMapMarkerPortalOfficeIdWithoutScroll = useCallback(
    (portalofficeId: number | null) => {
      if (portalofficeId !== null) {
        // ホバー開始の場合は即座に設定（スクロールなし）
        setMapMarkerPortalOfficeId(portalofficeId);
      }
      // ホバー解除の場合は何もしない（ONのまま維持）
    },
    [],
  );

  // マーカークリック時の処理（InfoWindow制御 + スクロール）
  const handleMarkerClick = useCallback(
    (portalofficeId: number) => {
      // InfoWindow の ON/OFF トグル
      if (mapMarkerPortalOfficeId === portalofficeId) {
        setMapMarkerPortalOfficeId(null);
      } else {
        setMapMarkerPortalOfficeId(portalofficeId);
        // 対応するListingにスクロール
        scrollToListing(portalofficeId, 'click');
      }
    },
    [mapMarkerPortalOfficeId, scrollToListing],
  );

  return {
    changeMode,
    method,
    hospitalMedicalCares,
    hospitalAppealPoints,
    search,
    isShowMap,
    toggleIsShowMap,
    gotoPageForPagination,
    localFavorites,
    setLocalFavorites,
    mapMarkerPortalOfficeId,
    setMapMarkerPortalOfficeIdWithoutScroll,
    handleMarkerClick,
    hospitalListingListRef,
  };
};
